import { MouseEvent } from 'react';
import { useBlocker, RegisteredRouter, useLocation } from '@tanstack/react-router';
import { LinkRouterProps } from '@anytag/router';
import { BaseModal, BaseModalProps } from '../modal';

// Default Prompt always has 2 buttons: Cancel (stay) and Confirm (navigating)
// Default Cancel button = stay, Submit button = navigate
// Use custom props function when you want to custom button onClick event
export type PromptBlockerResolver = ReturnType<typeof useBlocker<RegisteredRouter, true>>;

export interface PromptProps extends Omit<BaseModalProps, 'footer' | 'onClose'> {
  when: boolean; // Auto toggle Prompt when navigating
  open?: boolean; // Toggle Prompt manually
  limitPaths?: ReadonlyArray<LinkRouterProps['to']>; // Limit next pathnames which can trigger this Prompt, default any path can trigger this Prompt
  ignorePaths?: ReadonlyArray<LinkRouterProps['to']>; // Ignore the pathnames which can trigger this Prompt
  footer?: BaseModalProps['footer'] | ((props: PromptBlockerResolver) => BaseModalProps['footer']);
}

// TODO: Some cases missed, do more later
export const Prompt = ({
  footer,
  open = false,
  when = false,
  limitPaths = [],
  ignorePaths = [],
  ...restProps
}: PromptProps) => {
  const { pathname } = useLocation();
  const blockerResolver = useBlocker({
    disabled: !when,
    withResolver: true, // help return object instead of void when calling useBlocker
    shouldBlockFn: ({ next }) => {
      const isIgnoreCurrentPath = pathname === next.pathname;
      const isNotMatchLimitPaths =
        !!limitPaths.length &&
        // Remove trailing slash
        limitPaths.findIndex((path) => path?.replace(/\/$/, '') === next.fullPath.replace(/\/$/, '')) === -1;

      const isMatchIgnorePaths =
        !!ignorePaths.length &&
        // Remove trailing slash
        ignorePaths.findIndex((path) => path?.replace(/\/$/, '') === next.fullPath.replace(/\/$/, '')) !== -1;

      // Ignore current path and paths which not in limitPaths and ignorePaths
      if (isIgnoreCurrentPath || isNotMatchLimitPaths || isMatchIgnorePaths) {
        return false;
      }

      return true;
    }
  });

  const { proceed, reset, status } = blockerResolver;
  const promptFooter = (() => {
    if (typeof footer === 'function') {
      return footer(blockerResolver);
    }

    return {
      cancelButton: {
        ...footer?.cancelButton,
        onClick: (e: MouseEvent<HTMLButtonElement>) => {
          reset?.(); // Auto reset to close prompt
          footer?.cancelButton?.onClick?.(e); // Manual close prompt
        }
      },
      submitButton: { ...footer?.submitButton, onClick: proceed }
    } satisfies BaseModalProps['footer'];
  })();

  return status === 'blocked' || open ? (
    <BaseModal open withCloseIcon={false} footer={promptFooter} {...restProps} />
  ) : null;
};
